import { liveQuery, type Subscription } from 'dexie';
import { v7 } from 'uuid';
import { LiveValue, NetworkError, ProblemError, type HttpClient, type SessionService, type SessionLifetime, type SessionRetirement, type SessionState, type SignUpInput, type AccountEntry } from '@/model';
import { decodeMe, object } from './decode';
import { AccountStore } from './store';
import { webVersion } from './protocol';
import { admissionFor } from './capabilities';
import { bindDeviceRegistration } from './device-registration';
import { createHttpClient, type HttpAuth } from './http';
import { accountEntries, AccountsRegistry, decodeTokens, rotateRegistration, type AccountRegistration } from './session-registry';
export { AccountsRegistry, type AccountRegistration } from './session-registry';

interface SessionOptions {
  baseUrl?: string;
  fetch?: typeof fetch;
  onStart(registration: AccountRegistration, store: AccountStore): Promise<void>;
  onStop(): Promise<void>;
}
function getDeviceId(): string {
  try {
    const value = localStorage.getItem('hylo.web.device');
    if (value && /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(value)) return value;
    const next = v7(); localStorage.setItem('hylo.web.device', next); return next;
  } catch { return v7(); }
}
function deviceMetadata() {
  const ua = globalThis.navigator?.userAgent ?? '';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Web browser';
  const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Mac/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /Linux/.test(ua) ? 'Linux' : '';
  const osNumber = (/Mac OS X ([0-9_]+)/.exec(ua)?.[1] ?? /(?:iPhone|CPU) OS ([0-9_]+)/.exec(ua)?.[1] ?? /Windows NT ([0-9.]+)/.exec(ua)?.[1] ?? /Android ([0-9.]+)/.exec(ua)?.[1])?.replaceAll('_', '.');
  return { platform: 'web', name: Array.from(os ? `${browser} on ${os}` : browser).slice(0, 64).join(''), app_version: `${webVersion.name} (${webVersion.build})`, os_version: osNumber ? `${os} ${osNumber}` : os || 'Unknown', locale: globalThis.navigator?.language ?? 'en', time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' };
}

export class SessionController implements SessionService, HttpAuth {
  readonly state = new LiveValue<SessionState>({ phase: 'restoring' });
  readonly lifetime = new LiveValue<SessionLifetime | null>(null);
  private readonly retireHandlers = new Set<(event: SessionRetirement) => void | Promise<void>>();
  onRetire(handler: (event: SessionRetirement) => void | Promise<void>) { this.retireHandlers.add(handler); return () => { this.retireHandlers.delete(handler); }; }
  private lastDeleted?: string;
  private readonly acceptedDeletes = new Set<string>();
  private authAttempt = 0;
  readonly deviceId = getDeviceId();
  readonly registry = new AccountsRegistry();
  store: AccountStore | undefined;
  private http?: HttpClient;
  private registrationRecord?: AccountRegistration;
  private readonly baseUrl: string;
  private refreshFlight?: Promise<void>;
  private ending?: Promise<void>;
  private lifecycle = 0;
  private channel?: BroadcastChannel;
  private retiring = false;
  private readonly accountList = new LiveValue<AccountEntry[]>([]);
  private registryObserver?: Subscription;
  private profileObserver?: Subscription;
  private operations: Promise<unknown> = Promise.resolve();
  private badgeFlight?: Promise<void>;
  private readonly supportRegistrations = new Set<string>();
  private readonly deviceWrites = new Map<string, Promise<void>>();
  private disposed = false;
  private activationDepth = 0;
  private observing = false;
  accounts = () => { this.observeRegistry(); return this.accountList; };
  private serialized<T>(operation: () => Promise<T>): Promise<T> {
    const next = this.operations.then(operation, operation);
    this.operations = next.catch(() => undefined);
    return next;
  }
  private readonly focus = () => { void this.refreshAccountBadges(); };
  private readonly visible = () => { if (document.visibilityState === 'visible') this.focus(); };
  private observeRegistry() {
    if (this.observing || this.disposed) return;
    this.observing = true;
    this.registryObserver = liveQuery(() => this.registry.read(this.baseUrl)).subscribe({
      next: value => {
        this.publishEntries(accountEntries(value));
        const registration = this.registrationRecord;
        if (!registration || value.registrations.some(entry => entry.account_id === registration.account_id && entry.login_id === registration.login_id)) return;
        // Each tab keeps its selection. Only removal/replacement of its own login retires it.
        void this.serialized(async () => {
          if (this.registrationRecord?.login_id !== registration.login_id) return;
          const stored = await this.registry.accounts.get(registration.account_id);
          if (!stored || stored.login_id !== registration.login_id) { await this.clear('signedOut', false); await this.activateRemaining(new Set([registration.account_id])); }
        }).catch(() => undefined);
      },
      error: () => { /* Keep the original registry and retained stores on a storage failure. */ },
    });
    globalThis.addEventListener?.('focus', this.focus);
    globalThis.document?.addEventListener('visibilitychange', this.visible);
    if (globalThis.window?.BroadcastChannel) {
      this.channel = new BroadcastChannel('hylo.web.sessions');
      this.channel.onmessage = event => {
        const data = event.data as Record<string, unknown> | null;
        if (data?.type !== 'signedOut' || data.base_url !== this.baseUrl || data.account_id !== this.registrationRecord?.account_id || data.login_id !== this.registrationRecord?.login_id) return;
        void this.serialized(async () => {
          if (data.login_id === this.registrationRecord?.login_id) { await this.clear('signedOut', false, data.reason === 'deleted' ? 'deleted' : data.reason === 'remove' ? 'remove' : 'signOut'); await this.activateRemaining(new Set([String(data.account_id)])); }
        }).catch(() => undefined);
      };
    }
  }
  private publishEntries(entries: AccountEntry[]) {
    if (JSON.stringify(entries) !== JSON.stringify(this.accountList.get())) this.accountList.set(entries);
  }
  private async publishAccounts() { this.publishEntries(accountEntries(await this.registry.read(this.baseUrl))); }

  constructor(private readonly options: SessionOptions) { this.baseUrl = new URL(options.baseUrl ?? globalThis.location?.origin ?? 'http://localhost').origin; }
  setHttp(http: HttpClient) {
    this.http = http;
    bindDeviceRegistration(http, (lifetime, deviceId, body) => {
      const registration = this.registrationRecord;
      if (!registration || this.lifetime.get() !== lifetime || deviceId !== this.deviceId) return Promise.reject(new NetworkError('This account session has ended'));
      return this.writeDevice(registration, http, true, body);
    });
  }
  private api(): HttpClient { if (!this.http) throw new NetworkError('Session transport is unavailable'); return this.http; }
  generation = (): number => this.lifecycle;
  token = (): string | undefined => this.registrationRecord?.tokens.access_token;
  async accessToken(): Promise<string> {
    const lifecycle = this.lifecycle;
    if (!this.registrationRecord) throw new ProblemError({ status: 401, code: 'unauthorized' });
    if (!(Date.parse(this.registrationRecord.tokens.access_expires_at) > Date.now() + 30000)) await this.refresh();
    if (lifecycle !== this.lifecycle) throw new NetworkError('This account session has ended');
    const token = this.token(); if (!token) throw new ProblemError({ status: 401, code: 'unauthorized' }); return token;
  }
  restore(): Promise<void> {
    this.observeRegistry();
    const lifecycle = this.lifecycle;
    return this.serialized(async () => {
      if (lifecycle !== this.lifecycle || this.registrationRecord || this.disposed) return;
      try {
        // Cleanup is independent of which surviving account will be opened. A failed feature
        // cleanup keeps its receipt for the next launch, without hiding other valid sign-ins.
        for (const receipt of await this.registry.deletions.where('base_url').equals(this.baseUrl).toArray()) {
          if (!receipt.completed) await this.finishDeletion(receipt.account_id).catch(() => undefined);
        }
        await this.publishAccounts();
        const active = await this.registry.active.get(this.baseUrl);
        const selected = active ? await this.registry.accounts.get(active.account_id) : undefined;
        const registration = selected?.base_url === this.baseUrl ? selected : (await this.remainingRegistrations())[0];
        if (lifecycle !== this.lifecycle) return;
        if (!registration) { this.state.set({ phase: 'signedOut' }); return; }
        await this.activate(registration);
      } catch {
        // Preserve retained databases on storage/migration failure. Never erase them to get past a refusal.
        if (this.state.get().phase === 'restoring') this.state.set({ phase: 'signedOut' });
      }
    });
  }
  registration = () => this.api().get<{ mode: string; [extra: string]: unknown }>('/v1/auth/registration', { anonymous: true });
  addAccount(handle: string, password: string): Promise<void> { return this.signIn(handle, password); }
  async signIn(handle: string, password: string): Promise<void> {
    this.observeRegistry();
    const attempt = ++this.authAttempt;
    if (this.ending) await this.ending;
    const response = object(await this.api().post('/v1/auth/login', { handle, password, device: { id: this.deviceId, platform: 'web' } }, { anonymous: true }));
    if (attempt === this.authAttempt) await this.serialized(async () => { if (attempt === this.authAttempt) await this.accept(response, attempt); });
  }
  async signUp(input: SignUpInput): Promise<void> {
    this.observeRegistry();
    const attempt = ++this.authAttempt;
    if (this.ending) await this.ending;
    const response = object(await this.api().post('/v1/auth/signup', { handle: input.handle, password: input.password, ...(input.displayName ? { display_name: input.displayName } : {}), ...(input.invitationCode ? { invitation_code: input.invitationCode } : {}), device: { id: this.deviceId, platform: 'web' } }, { anonymous: true }));
    if (attempt === this.authAttempt) await this.serialized(async () => { if (attempt === this.authAttempt) await this.accept(response, attempt); });
  }
  private async accept(response: Record<string, unknown>, attempt: number): Promise<void> {
    const me = decodeMe(response.me);
    const registration: AccountRegistration = { account_id: me.account.id, base_url: this.baseUrl, me, tokens: decodeTokens(response.tokens), login_id: v7() };
    const previousId = this.registrationRecord?.account_id;
    if (this.registrationRecord) await this.clear('signedOut', false, 'signOut', true);
    if (attempt !== this.authAttempt) return;
    await this.registry.transaction('rw', this.registry.accounts, this.registry.active, this.registry.identities, async () => { if (attempt !== this.authAttempt) return; await this.registry.accounts.put(registration); await this.registry.identities.put({ ...(await this.registry.identities.get(registration.account_id)), account_id: registration.account_id, base_url: this.baseUrl, me, last_active_at: Date.now(), expired: false }); await this.registry.active.put({ base_url: this.baseUrl, account_id: registration.account_id }); });
    if (attempt !== this.authAttempt) {
      await this.registry.transaction('rw', this.registry.accounts, this.registry.active, async () => { if ((await this.registry.accounts.get(registration.account_id))?.login_id === registration.login_id) { await this.registry.accounts.delete(registration.account_id); await this.registry.active.delete(this.baseUrl); } });
      return;
    }
    await this.publishAccounts();
    await this.activate(registration, true, previousId);
    if (this.registrationRecord?.login_id === registration.login_id) {
      try { await this.refreshDevice(); }
      catch (error) {
        if (this.registrationRecord?.login_id !== registration.login_id) return;
        if (error instanceof ProblemError && (error.status === 401 || error.code === 'account_suspended')) throw error;
        // Device metadata is retried on reconnect; an outage must not discard a successful login.
      }
    }
  }

  private async activate(registration: AccountRegistration, fresh = false, previousId?: string): Promise<void> {
    try { await this.tryActivate(registration, fresh); }
    catch (error) { await this.activateRemaining(new Set([registration.account_id]), previousId); throw error; }
  }
  private async tryActivate(registration: AccountRegistration, fresh = false): Promise<void> {
    this.activationDepth++;
    try {
      await this.openRegistration(registration, fresh);
      const state = this.state.get();
      if (this.disposed || state.phase !== 'signedIn' || state.accountId !== registration.account_id || !this.store || this.registrationRecord?.account_id !== registration.account_id) throw new NetworkError('This account session has ended');
    } catch (error) {
      // A failed open/start must close partial runtime resources, retaining the original database.
      if (this.registrationRecord?.account_id === registration.account_id) await this.clear('signedOut', false, 'signOut', true).catch(() => undefined);
      throw error;
    } finally { this.activationDepth--; }
  }
  private async openRegistration(registration: AccountRegistration, fresh = false): Promise<void> {
    if (await this.registry.deletions.get(registration.account_id)) throw new ProblemError({ status: 401, code: 'account_deleted' });
    const lifecycle = ++this.lifecycle; this.retiring = false; this.refreshFlight = undefined;
    admissionFor(this.api())?.reset();
    if (!registration.login_id) { registration = { ...registration, login_id: v7() }; await this.registry.accounts.put(registration); }
    if (lifecycle !== this.lifecycle) return;
    this.registrationRecord = registration;
    const db = new AccountStore(registration.account_id); this.store = db;
    await db.open();
    const valid = () => lifecycle === this.lifecycle && this.store === db;
    if (!valid()) { db.close(); return; }
    if (fresh || !await db.me.get('me')) await db.me.put({ id: 'me', value: registration.me });
    if (!valid()) return;
    if (fresh || !await db.accounts.get(registration.account_id)) await db.accounts.put(registration.me.account);
    if (!valid()) return;
    const me = (await db.me.get('me'))?.value ?? registration.me;
    const installed = await this.registry.transaction('rw', this.registry.accounts, this.registry.identities, this.registry.active, async () => {
      if (!valid() || (await this.registry.accounts.get(registration.account_id))?.login_id !== registration.login_id) return false;
      await this.registry.active.put({ base_url: this.baseUrl, account_id: registration.account_id });
      await this.registry.identities.put({ ...(await this.registry.identities.get(registration.account_id)), account_id: registration.account_id, base_url: this.baseUrl, me, last_active_at: Date.now(), expired: false });
      return true;
    });
    if (!valid()) return;
    if (!installed) { await this.clear('revoked', false); return; }
    this.profileObserver = liveQuery(() => db.me.get('me')).subscribe({ next: row => {
      if (!row || !valid()) return;
      this.registrationRecord = { ...this.registrationRecord!, me: row.value };
      void this.registry.identities.update(registration.account_id, { me: row.value }).catch(() => undefined);
    }, error: () => undefined });
    this.lifetime.set({ accountId: registration.account_id, loginId: registration.login_id!, generation: this.lifecycle });
    this.state.set({ phase: 'signedIn', accountId: registration.account_id, handle: registration.me.account.handle });
    await this.options.onStart(registration, db);
    await this.publishAccounts();
    if (valid()) void this.refreshAccountBadges();
  }
  refresh = async (): Promise<void> => {
    if (this.retiring) throw new NetworkError('This account session is ending');
    if (this.refreshFlight) return this.refreshFlight;
    const registration = this.registrationRecord; const lifecycle = this.lifecycle;
    if (!registration) throw new ProblemError({ status: 401, code: 'unauthorized' });
    const current = () => lifecycle === this.lifecycle && this.registrationRecord?.account_id === registration.account_id && this.registrationRecord.login_id === registration.login_id;
    this.refreshFlight = rotateRegistration(this.registry, this.api(), this.deviceId, registration, current).then(next => {
      if (current()) this.registrationRecord = next;
    }).catch(async error => {
      if (current() && error instanceof ProblemError && (error.status === 401 || error.code === 'account_suspended')) await this.rejected(error, lifecycle);
      throw error;
    }).finally(() => { if (lifecycle === this.lifecycle) this.refreshFlight = undefined; });
    return this.refreshFlight;
  };
  rejected = async (error: ProblemError, generation = this.lifecycle): Promise<void> => {
    if (generation !== this.lifecycle || !this.registrationRecord || this.retiring) return;
    const reason = error.code === 'account_suspended' ? 'suspended' : error.code === 'refresh_reused' || error.code === 'session_revoked' || error.code === 'auth_session_revoked' ? 'revoked' : 'expired';
    const accountId = this.registrationRecord.account_id;
    await this.clear(reason);
    // Rejections can arrive from onStart inside the transition queue. That activation recovers
    // inline; later failures enqueue recovery without waiting on their own request/transition.
    if (!this.activationDepth && !this.disposed) void this.serialized(async () => {
      if (!this.registrationRecord && !this.disposed) await this.activateRemaining(new Set([accountId]));
    }).catch(() => undefined);
  };
  signOut(options: { removeLocalData?: boolean } = {}): Promise<void> {
    const accountId = this.registrationRecord?.account_id;
    if (!accountId) { this.authAttempt++; return this.clear('signedOut'); }
    return this.signOutSelected(accountId, options);
  }
  switchTo(accountId: string): Promise<void> {
    this.authAttempt++;
    this.observeRegistry();
    return this.serialized(async () => {
      const registration = await this.registry.accounts.get(accountId);
      if (!registration || registration.base_url !== this.baseUrl) throw new ProblemError({ status: 401, code: 'unauthorized' });
      if (this.registrationRecord?.account_id === accountId && this.registrationRecord.login_id === registration.login_id) return;
      const previousId = this.registrationRecord?.account_id;
      if (this.registrationRecord) await this.clear('signedOut', false, 'signOut', true);
      await this.activate(registration, false, previousId);
    });
  }
  /** A closed account's client can never read the active account's bearer token. */
  private accountClient(registration: AccountRegistration): HttpClient {
    let held = registration;
    return createHttpClient({ baseUrl: this.baseUrl, fetch: this.options.fetch, auth: {
      token: () => held.tokens.access_token,
      refresh: async () => { held = await rotateRegistration(this.registry, this.api(), this.deviceId, held, () => !this.disposed); },
      // A failed background badge read must preserve both the sign-in and its last known badge (accounts.md §5).
      rejected: async () => undefined,
    } });
  }
  refreshAccountBadges(): Promise<void> {
    if (this.disposed) return Promise.resolve();
    if (this.badgeFlight) return this.badgeFlight;
    this.badgeFlight = (async () => {
      const registrations = await this.registry.accounts.where('base_url').equals(this.baseUrl).toArray();
      await Promise.allSettled(registrations.filter(entry => entry.account_id !== this.registrationRecord?.account_id).map(async registration => {
        const identity = await this.registry.identities.get(registration.account_id);
        const client = this.accountClient(registration);
        const result = await client.get<{ badge: number }>('/v1/me/badge', { cause: 'background' });
        await this.registerSupportDevice(registration, client);
        if (!Number.isSafeInteger(result?.badge) || result.badge < 0 || this.disposed || this.registrationRecord?.account_id === registration.account_id) return;
        await this.registry.transaction('rw', this.registry.accounts, this.registry.identities, async () => {
          const current = await this.registry.accounts.get(registration.account_id), metadata = await this.registry.identities.get(registration.account_id);
          if (current?.login_id !== registration.login_id || metadata?.last_active_at !== identity?.last_active_at || !metadata) return;
          await this.registry.identities.update(registration.account_id, { badge: result.badge });
        });
      }));
      if (!this.disposed) await this.publishAccounts();
    })().catch(() => undefined).finally(() => { this.badgeFlight = undefined; });
    return this.badgeFlight;
  }
  signOutAccount(accountId: string): Promise<void> { return this.signOutSelected(accountId); }
  private signOutSelected(accountId: string, options: { removeLocalData?: boolean } = {}): Promise<void> {
    this.authAttempt++;
    return this.serialized(async () => {
      const registration = await this.registry.accounts.get(accountId);
      const identity = await this.registry.identities.get(accountId);
      if ((!registration || registration.base_url !== this.baseUrl) && (!identity || identity.base_url !== this.baseUrl)) return;
      const active = this.registrationRecord?.account_id === accountId;
      if (options.removeLocalData && !active) throw new NetworkError('This account session has ended');
      try {
        if (registration) {
          if (active) {
            await this.accessToken(); this.retiring = true; await this.options.onStop();
            await this.api().post('/v1/auth/logout');
          } else await this.accountClient(registration).post('/v1/auth/logout');
        }
      } finally {
        if (active) {
          await this.clear('signedOut', true, options.removeLocalData ? 'remove' : 'signOut');
          await this.activateRemaining();
        } else {
          const removed = await this.registry.transaction('rw', this.registry.accounts, this.registry.active, this.registry.identities, async () => {
            const current = await this.registry.accounts.get(accountId);
            if (current?.login_id !== registration?.login_id) return false;
            await this.registry.accounts.delete(accountId);
            if ((await this.registry.active.get(this.baseUrl))?.account_id === accountId) await this.registry.active.delete(this.baseUrl);
            if (identity) await this.registry.identities.put({ ...identity, expired: false });
            return true;
          });
          if (removed) {
            this.channel?.postMessage({ type: 'signedOut', account_id: accountId, login_id: registration?.login_id, reason: 'signOut', base_url: this.baseUrl });
            // No active lifetime is being retired in this tab. Existing onRetire consumers (uploads,
            // sockets and feature scopes) belong to the active account and must keep running.
          }
          await this.publishAccounts();
        }
      }
    });
  }
  private async activateRemaining(excluded: ReadonlySet<string> = new Set(), previousId?: string) {
    if (this.disposed || this.registrationRecord) return;
    const candidates = await this.remainingRegistrations();
    candidates.sort((a, b) => Number(b.account_id === previousId) - Number(a.account_id === previousId));
    for (const registration of candidates) {
      if (excluded.has(registration.account_id) || this.disposed) continue;
      try { await this.tryActivate(registration); return; }
      catch { /* Try another saved login; a broken retained store stays intact. */ }
    }
  }
  private async remainingRegistrations() {
    const { registrations, identities } = await this.registry.read(this.baseUrl);
    const recency = new Map(identities.map(entry => [entry.account_id, entry.last_active_at ?? 0]));
    return registrations.sort((a, b) => (recency.get(b.account_id) ?? 0) - (recency.get(a.account_id) ?? 0));
  }
  /** Release a service container without revoking any saved sign-ins or deleting retained work. */
  async dispose(): Promise<void> {
    this.disposed = true; this.authAttempt++;
    this.registryObserver?.unsubscribe(); this.profileObserver?.unsubscribe();
    globalThis.removeEventListener?.('focus', this.focus);
    globalThis.document?.removeEventListener('visibilitychange', this.visible);
    this.channel?.close(); this.channel = undefined;
    await this.operations;
    await this.clear('signedOut', false, 'signOut', true);
    await this.badgeFlight;
    this.registry.close();
  }
  deleteAccount(password: string): Promise<void> {
    const lifecycle = this.lifecycle;
    const accountId = this.registrationRecord?.account_id ?? this.lastDeleted;
    // Keep the authorizing lifetime until its DELETE answer and acceptance receipt are durable.
    // Capture before queuing so a stale caller cannot instead delete the destination account.
    return this.serialized(() => this.deleteCapturedAccount(password, accountId, lifecycle));
  }
  private async deleteCapturedAccount(password: string, accountId: string | undefined, lifecycle: number): Promise<void> {
    if (!accountId) throw new ProblemError({ status: 401, code: 'unauthorized' });
    const accepted = await this.registry.deletions.get(accountId);
    if (lifecycle !== this.lifecycle) throw new NetworkError('This account session has ended');
    if (accepted || this.acceptedDeletes.has(accountId)) { await this.finishDeletion(accountId); return; }
    await this.accessToken();
    if (lifecycle !== this.lifecycle) throw new NetworkError('This account session has ended');
    const registration = this.registrationRecord!;
    this.retiring = true; await this.options.onStop();
    try {
      if (lifecycle !== this.lifecycle) throw new NetworkError('This account session has ended');
      await this.api().delete('/v1/me', { body: { password } });
    } catch (error) {
      if (lifecycle === this.lifecycle) {
        this.retiring = false;
        if (error instanceof ProblemError && (error.code === 'account_suspended' || error.status === 401 && error.code !== 'invalid_credentials')) await this.rejected(error);
        else if (this.registrationRecord && this.store) await this.options.onStart(this.registrationRecord, this.store);
      }
      throw error;
    }
    // This receipt is separate from local cleanup: an accepted DELETE is never retried for cleanup failures.
    this.lastDeleted = accountId; this.acceptedDeletes.add(accountId);
    try { await this.registry.deletions.put({ account_id: accountId, base_url: this.baseUrl, login_id: registration.login_id!, completed: false }); }
    finally { if (lifecycle === this.lifecycle) { await this.clear('signedOut', true, 'deleted').catch(() => undefined); await this.activateRemaining(); } }
  }
  private async finishDeletion(accountId: string): Promise<void> {
    const receipt = await this.registry.deletions.get(accountId);
    if (!receipt || receipt.base_url !== this.baseUrl || receipt.completed) return;
    if (this.registrationRecord?.account_id === accountId) { await this.clear('signedOut', true, 'deleted').catch(() => undefined); return; }
    if (this.registrationRecord) return; // Startup recovery runs before any surviving login's runtime opens.
    const registration = await this.registry.accounts.get(accountId);
    // An accepted deletion is terminal for this account id, even if feature cleanup was interrupted.
    await this.registry.transaction('rw', this.registry.accounts, this.registry.active, this.registry.identities, async () => {
      await this.registry.accounts.delete(accountId);
      if ((await this.registry.active.get(this.baseUrl))?.account_id === accountId) await this.registry.active.delete(this.baseUrl);
      await this.registry.identities.delete(accountId);
    });
    if (registration) this.channel?.postMessage({ type: 'signedOut', account_id: accountId, login_id: registration.login_id, reason: 'deleted', base_url: this.baseUrl });
    const results = await Promise.allSettled([...this.retireHandlers].map(handler => Promise.resolve().then(() => handler({ accountId, reason: 'deleted' }))));
    if (results.some(result => result.status === 'rejected')) return;
    const store = new AccountStore(accountId); await store.delete();
    if (globalThis.caches) await caches.delete(`hylo-web-media-${accountId}`);
    await this.registry.deletions.update(accountId, { completed: true });
  }
  private async clear(reason: 'signedOut' | 'expired' | 'revoked' | 'suspended', broadcast = true, retirement: SessionRetirement['reason'] = reason === 'signedOut' ? 'signOut' : 'revoked', retainCredentials = false): Promise<void> {
    if (this.ending) return this.ending;
    const registration = this.registrationRecord; const store = this.store;
    const remove = retirement === 'remove' || retirement === 'deleted';
    if (broadcast && registration) this.channel?.postMessage({ type: 'signedOut', account_id: registration.account_id, login_id: registration.login_id, reason: retirement, base_url: this.baseUrl });
    this.profileObserver?.unsubscribe(); this.profileObserver = undefined;
    this.registrationRecord = undefined; this.store = undefined; this.lifecycle++; this.refreshFlight = undefined; this.retiring = true;
    admissionFor(this.api())?.reset();
    this.lifetime.set(null); this.state.set({ phase: 'signedOut', reason });
    this.ending = (async () => {
      try {
        await this.options.onStop();
        if (registration) {
          let superseded = false;
          await this.registry.transaction('rw', this.registry.accounts, this.registry.active, this.registry.identities, async () => {
            const current = await this.registry.accounts.get(registration.account_id);
            superseded = !!current && current.login_id !== registration.login_id;
            if (!superseded && !retainCredentials) {
              await this.registry.accounts.delete(registration.account_id);
              if ((await this.registry.active.get(this.baseUrl))?.account_id === registration.account_id) await this.registry.active.delete(this.baseUrl);
              if (remove) await this.registry.identities.delete(registration.account_id);
              else await this.registry.identities.put({ ...(await this.registry.identities.get(registration.account_id)), account_id: registration.account_id, base_url: this.baseUrl, me: registration.me, expired: reason !== 'signedOut' });
            }
          });
          const eventReason = superseded ? 'signOut' as const : retirement;
          const results = await Promise.allSettled([...this.retireHandlers].map(handler => Promise.resolve().then(() => handler({ accountId: registration.account_id, reason: eventReason }))));
          if (superseded) return;
          if (remove) {
            if (globalThis.caches) await caches.delete(`hylo-web-media-${registration.account_id}`);
            await (store ?? new AccountStore(registration.account_id)).delete();
            const failed = results.find(result => result.status === 'rejected');
            if (failed?.status === 'rejected') throw failed.reason;
            if (retirement === 'deleted') await this.registry.deletions.update(registration.account_id, { completed: true });
          }
        }
      } finally { store?.close(); this.retiring = false; await this.publishAccounts(); }
    })().finally(() => { this.ending = undefined; });
    return this.ending;
  }
  finishSignOut(): Promise<void> { return this.clear('signedOut'); }
  private registerSupportDevice(registration: AccountRegistration, client: HttpClient): Promise<void> {
    return this.writeDevice(registration, client, false);
  }
  /** All metadata and capability writes share an account chain. A late old PUT must never clear support.v1. */
  private writeDevice(registration: AccountRegistration, client: HttpClient, allowLegacy: boolean, body?: Record<string, unknown>): Promise<void> {
    const key = `${registration.account_id}:${registration.login_id}:${this.deviceId}`;
    const requestedSupport = admissionFor(client)?.state.get().includes('support.v1') === true;
    const lifecycle = this.lifecycle;
    const previous = this.deviceWrites.get(registration.account_id) ?? Promise.resolve();
    const task = previous.catch(() => undefined).then(async () => {
      const activeCurrent = () => !this.disposed && (client !== this.api() || lifecycle === this.lifecycle && this.registrationRecord?.login_id === registration.login_id);
      const current = async () => activeCurrent() && (await this.registry.accounts.get(registration.account_id))?.login_id === registration.login_id;
      if (!await current() || !activeCurrent() || !body && this.supportRegistrations.has(key)) return;
      // Re-evaluate after the previous write: its response may have acknowledged Support.
      const support = this.supportRegistrations.has(key) || requestedSupport || admissionFor(client)?.state.get().includes('support.v1') === true;
      if (!allowLegacy && !support) return;
      const metadata: Record<string, unknown> = body ? { ...body } : deviceMetadata();
      delete metadata.capabilities; if (support) metadata.capabilities = ['support.v1'];
      try { await client.put(`/v1/me/devices/${this.deviceId}`, metadata); }
      catch (error) {
        if (!await current() || !activeCurrent()) return;
        if (!body && error instanceof ProblemError && error.status === 400) await client.put(`/v1/me/devices/${this.deviceId}`, { ...metadata, name: 'Web browser' });
        else throw error;
      }
      if (support && await current()) this.supportRegistrations.add(key);
    }).finally(() => { if (this.deviceWrites.get(registration.account_id) === task) this.deviceWrites.delete(registration.account_id); });
    this.deviceWrites.set(registration.account_id, task); return task;
  }
  async refreshDevice(): Promise<void> {
    const registration = this.registrationRecord; if (!registration) return;
    await this.writeDevice(registration, this.api(), true);
  }
}
